import { supertypeClass, Bindable, Remoteable, Persistable, Supertype } from '@haventech/amorphic';
import { Product } from '../../../../../common/js/static/Product';
import { Utils } from '../../../../../common/js/Utils';
import { RateClasses } from '../../../../../common/js/static/RateClasses';
import { constants } from '../../basicIllustration/templates/constantsForPdf';
import { IllustrationRequest } from '../../IllustrationRequest';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { HtmlPdfUtils } from '../../HtmlPdfUtils';
import { columnHeaderDefinitions } from '../../basicIllustration/templates/narrativesummary/info/ColumnHeadingDefinitions';
import { wholeLifeColumns } from '../../IfiConstants';

import { CoverpathUtilsBackEnd } from '../../../../js/static/CoverpathUtilsBackEnd';

import {
  PaymentTerms,
  ProductType,
  PaymentOption,
  WholeLifePaymentTerms,
  PaymentOptionWholeLife,
  AdditionalData,
  mmopmProductBlocks,
} from '../../HtmlPdfTypes';
import { CommonUtilsBackend } from '../../../../../common/js/static/CommonUtilsBackend';

export class CssStyle {
  HtmlPdfNarrativeSummaryStyle: string = '';
}

class NarrativeSummary {
  HtmlPdfNarrativeSummary: string = '';
  HtmlPdfNarrativeSummaryPageTwo: string = '';
  HtmlPdfNarrativeSummaryPageThree: string = '';
  HtmlPdfNarrativeSummaryProductDescription: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionART: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionECP: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionECP15: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionMMOPMTerm: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionT20G99: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionMMOPMART: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionAPT80: string = '';
  HtmlPdfNarrativeSummaryWaiverDescription: string = '';
  HtmlPdfNarrativeSummaryWaiverColumnDescription: string = '';
  HtmlPdfNarrativeSummaryMMOPMTermNonGuaranteedColumnDescriptions: string = '';
  HtmlPdfNarrativeSummaryMMOPMAPT80NonGuaranteedColumnDescriptions: string = '';
  HtmlPdfNarrativeSummaryRiderList: string = '';
  HtmlPdfNarrativeSummaryFaceAmountDecrease: string = '';
  HtmlPdfNarrativeSummaryWholeLifeFaceChange: string = '';
  HtmlPdfNarrativeSummaryWholeLifeFaceDecreaseDisclaimer: string = '';
  HtmlPdfNarrativeSummaryShortARTDescription: string = '';
  HtmlPdfNarrativeSummaryShortOPMARTDescription: string = '';
  HtmlPdfNarrativeSummaryShortAPT80Description: string = '';
  HtmlPdfNarrativeSummaryShortT20GDescription: string = '';
  HtmlPdfNarrativeSummaryShortT20G99Description: string = '';
  HtmlPdfNarrativeSummaryShortProductDescription: string = '';
  HtmlPdfNarrativeSummaryABRDescription: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionWholeLife: string = '';
  HtmlPdfNarrativeSummaryWholeLife: string = '';
  HtmlPdfNarrativeSummaryRider: string = '';
  HtmlPdfNarrativeSummaryPageTwoWholeLife: string = '';
  HtmlPdfNarrativeSummaryPageTwoMec: string = '';
  HtmlPdfNarrativeSummaryPageTwoNoMec: string = '';
  HtmlPdfNarrativeSummaryPageTwoSupplementalMec: string = '';
  HtmlPdfNarrativeSummaryPageTwoSupplementalNoMec: string = '';
  HtmlPdfNarrativeSummaryPageThreeWholeLife: string = '';
  HtmlPdfNarrativeSummaryRiderDefinition: string = '';
  HtmlPdfNarrativeSummaryProductDescriptionMMTRADTermECP: string = '';
  css: CssStyle = new CssStyle();
}

const riderDefinition = {
  GIR: {
    title: 'Guaranteed Insurability Rider (GIR):',
    description:
      'This rider provides for the right to purchase life insurance without evidence of insurability in limited amounts over time. Premiums for this rider are payable to age 46 or the end of the premium paying period of the policy, if earlier.',
  },
  RTR: {
    title: 'Renewable Term Rider (RTR):',
    description:
      "This rider provides a level amount of term insurance. The term coverage is renewed automatically each year until the policy anniversary nearest the insured's age 95. Premiums increase on renewal and are payable to age 95. There is a schedule of increasing guaranteed term charges set at issue that are reflected in the Contract Premium for Guaranteed Values on the illustration. Current charges are shown in the non-guaranteed Contract Premium on the illustration. Based on input in this illustration, Renewable Term Rider coverage ceases in year {{RTR_END_YEAR}}.",
  },
  ALIR: {
    title: 'Additional Life	Insurance Rider (ALIR):',
    description:
      'This	rider provides	for the	purchase of additional	 participating paid-up life insurance coverage, based upon the amount of the rider payment made,	and net of the payment expense charge (equal to 7.5% of	any rider payment).	The payments	for this rider can be scheduled, recurring payments	or one-time unscheduled payments. Payments	can be	made each year until the policy is paid-up. Scheduled	payments are selected by the policyowner at time of application. The minimum initial scheduled and unscheduled payment	 amount must each be at least $300 annually. This illustration assumes the rider payments	are made as shown in	 the illustration. If the ALIR payments are lower or made later than illustrated, the policy values will be lower than what is shown in this illustration. ALIR payments and dividends are based on the dates payments are applied to your policy. If the date ALIR payments are applied	 to the policy are different than what is illustrated, the policy	 values will be different than what is illustrated. Please refer to the ALIR Information page of this illustration for additional details and limitations.',
  },
  TIR: {
    title: 'Transfer of Insured Rider (TIR):',
    description:
      'The Transfer of Insured Rider provides the policyowner with the right to transfer or exchange a new insured in place of the current insured under the policy, provided an insurable interest exists between the owner and the substitute insured, the new insured is not older than age 75 and evidence of insurability is provided. There is no annual premium for this rider however there is a cost due if the rider is exercised.',
  },
};

@supertypeClass({ toClient: true, toServer: true })
export class HtmlPdfNarrativeSummary extends Bindable(Remoteable(Persistable(Supertype))) {
  private templateFolder = './apps/coverpath-common/classes/wholelife/components/narrativesummary/templates/';

  async getHtmlContent_NarrativeSummary(
    illustrationRequest: IllustrationRequest,
    basicIllustrationAdditionalData: AdditionalData,
    inforceProductType: ProductType,
    wholeLifePaymentOptions?: WholeLifePaymentTerms
  ) {
    const paymentOptions: PaymentTerms = basicIllustrationAdditionalData.paymentOptions;
    const productBlock: string = basicIllustrationAdditionalData.productBlock;
    const date = CoverpathUtils.getDateInPrettyFormat();
    const timezone = await CommonUtilsBackend.getTimezoneIdByZipcode(illustrationRequest.agent.address.zip);
    const time = CoverpathUtilsBackEnd.getTimeInPrettyFormat(timezone);
    const ns = await HtmlPdfUtils.getContent(this.templateFolder, new NarrativeSummary());
    const productCode = illustrationRequest.productCode;
    const term = illustrationRequest.term;
    const isCAS = HtmlPdfUtils.isCASProduct(productCode);
    const displayFaceAmount =
      illustrationRequest.newFaceAmount > 0 && illustrationRequest.faceAmount > illustrationRequest.newFaceAmount
        ? Utils.formatCurrency(illustrationRequest.newFaceAmount, 0, false)
        : Utils.formatCurrency(illustrationRequest.faceAmount, 0, false);

    const dividendOptionMap = {
      PUA: {
        name: 'Paid-up Additions',
        intro: 'This illustration assumes that the dividend option is Paid Up Additions for all years shown.',
        paragraph:
          'The annual dividend, if any, is used to buy additional level paid-up life insurance, adding to the policy\'s death benefit and total cash value. "Paid-up" means that no further premiums are required on the additional life insurance. This additional insurance is also eligible to receive dividends. Paid-up additions may be surrendered for their cash value as long as they are not being used as collateral for policy debt.',
      },
      Cash: {
        name: 'Cash',
        intro: 'This illustration assumes that the dividend option is Cash for all years shown.',
        paragraph: 'The annual dividend, if any, is paid to the policyowner in cash.',
      },
      PRPUA: {
        name: 'Reduce Premium/Paid-up Additions',
        intro:
          'This illustration assumes that the dividend option is Reduce Premiums with excess to Paid Up Additions for all years shown.',
        paragraph:
          "The prior year's annual dividend, if any, will be used to reduce premium payments. If the dividend is not enough to pay the full premium due, the balance must be paid by the end of the grace period. If this balance is not paid, we will use the dividend to pay the next smaller premium (semiannual or quarterly - but not less than quarterly) that it will cover. Premiums will then be payable at this new frequency. If the dividend exceeds the billed premium, any excess will be used to purchase additional insurance",
      },
      PRCash: {
        name: 'Reduce Premium/Cash',
        intro:
          'This illustration assumes that the dividend option is Reduce Premiums with excess to cash for all years shown.',
        paragraph:
          "The prior year's annual dividend, if any, will be used to reduce premium payments. If the dividend is not enough to pay the full premium due, the balance must be paid by the end of the grace period. If this balance is not paid, we will use the dividend to pay the next smaller premium that it will cover. Premiums will then be payable at this new frequency. If the dividend exceeds the billed premium, any excess will be sent to you in cash.",
      },
      Accumulate: {
        name: 'Dividend Accumulations',
        intro: 'This illustration assumes that the dividend option is Accumulate at Interest for all years shown.',
        paragraph:
          'Under this option, dividends, if any, are held by the company and earn interest that is credited to the policy at the end of each policy year. Interest will be at an annual rate determined by us (but not less than 2 ½%) and will be compounded annually. Dividend accumulations may be withdrawn as long as they are not being used as collateral for policy debt. If dividends are left in the policy to accumulate at interest, the interest credited each year is treated as taxable income, which MassMutual must report to the Internal Revenue Service. Policyowners who select this dividend option will receive a 1099 form for the taxable interest after the end of each tax year.',
      },
    };

    let description = '';
    let longName;
    const style = `${ns.css.HtmlPdfNarrativeSummaryStyle}`;
    let body;

    // this is where all the action happens get the proper description, from there place that description in the body,
    // then get the HTML content and return that to htmlPdfContentController
    if (inforceProductType === ProductType.Term) {
      description = getTermDescription();
      body = getTermBody();
    } else if (inforceProductType === ProductType.WholeLife) {
      description = getWholeLifeDescription();
      body = getWholeLifeBody();
    }

    return HtmlPdfUtils.getHtmlContent(style, body);

    // the nested functions handle most of the work they are nested within getHtmlContent_NarrativeSummary so they can share/access to all of the local constants/variables

    // ******
    // Section : Whole Life functions
    // ****

    // combines all the other templates/functions into one HTML body that is returned as a string
    function getWholeLifeBody() {
      return ns.HtmlPdfNarrativeSummaryWholeLife.replace(/{{ProductDescription}}/g, description)
        .replace(/{{Date}}/, date)
        .replace(/{{Time}}/g, time)
        .replace(/{{ClientFirstName}}/g, illustrationRequest.customerPerson.firstName)
        .replace(/{{ClientLastName}}/g, illustrationRequest.customerPerson.lastName)
        .replace(
          /{{ClientGender}}/g,
          HtmlPdfUtils.getGenderInPrettyFormat(illustrationRequest.gender, illustrationRequest.state)
        )
        .replace(
          /{{ClientAge}}/g,
          CoverpathUtils.getInsuranceAge(illustrationRequest.dob, illustrationRequest.startDate)
        )
        .replace(/{{UnderwritingClass}}/g, RateClasses[illustrationRequest.rateClass].desc)
        .replace(/{{PolicyFormNumber}}/g, HtmlPdfUtils.getWholeLifePolicyFormNumber(illustrationRequest.state))
        .replace(/{{PolicyNumber}}/g, illustrationRequest.policyNumber)
        .replace(/{{PolicyDate}}/g, Utils.formatDateLong(new Date(illustrationRequest.startDate)))
        .replace(/{{PaymentFrequency}}/g, Utils.capitalizeFirstLetter(illustrationRequest.paymentFrequency))
        .replace(
          /{{PremiumRiders}}/g,
          getPremiumRiders(illustrationRequest, wholeLifePaymentOptions[illustrationRequest.paymentFrequency])
        )
        .replace(
          /{{Premium}}/g,
          `$${Utils.formatNumber(Utils.round(paymentOptions[illustrationRequest.paymentFrequency].total, 2), 2)}`
        )
        .replace(/{{Term}}/g, term)
        .replace(/{{PayableTermPhrase}}/g, getPayableTermPhrase())
        .replace(/{{GenericPolicyName}}/g, getWholeLifePolicyName())
        .replace(/{{DividendOption}}/g, dividendOptionMap[illustrationRequest.dividendOption].name)
        .replace(/{{FaceAmount}}/g, Utils.formatCurrency(illustrationRequest.faceAmount))
        .replace(/{{Rider}}/g, getCoverageInformationRiders())
        .replace(/{{Intro}}/g, dividendOptionMap[illustrationRequest.dividendOption].intro)
        .replace(/{{Paragraph}}/g, dividendOptionMap[illustrationRequest.dividendOption].paragraph)
        .replace(/{{Year}}/g, CoverpathUtils.getCurrentRateYear())
        .replace(/{{DeathBenefit}}/g, `$${displayFaceAmount}`);
    }

    // loops through riders from request and, if they exist, adds them to the coverage information breakdown
    // applies a HtmlPdfNarrativeSummaryRider template for each rider present
    function getPremiumRiders(
      illustrationRequest: IllustrationRequest,
      wholeLifePaymentOptions: PaymentOptionWholeLife
    ) {
      let riderHtml = '';
      riderHtml =
        riderHtml +
        ns.HtmlPdfNarrativeSummaryRider.replace(/{{Title}}/, 'Base Premium').replace(
          /{{Data}}/,
          Utils.formatCurrency(wholeLifePaymentOptions.basePolicyPremium)
        );

      // If there are waiver premium values in the policy admin response, show them
      if (wholeLifePaymentOptions.waiverPremium) {
        riderHtml =
          riderHtml +
          ns.HtmlPdfNarrativeSummaryRider.replace(/{{Title}}/, 'Waiver of Premium (WP)').replace(
            /{{Data}}/,
            Utils.formatCurrency(getWaiverPremium(wholeLifePaymentOptions))
          );
      }
      if (illustrationRequest.riders && illustrationRequest.riders.length > 0) {
        illustrationRequest.riders.forEach((rider) => {
          let title;
          let currPremium;
          switch (rider.type) {
            case 'GIR':
              title = 'Guaranteed Insurability Rider (GIR) Premium';
              currPremium = wholeLifePaymentOptions.GIRPremium;
              break;
            case 'ALIR':
              title = 'Additional Life Insurance (ALIR) Scheduled Purchase Payment';
              currPremium = rider.amount; // ALIR comes from request until PA is ready
              break;
            case 'RTR':
              title = 'Renewable Term Rider (RTR) Premium';
              currPremium = wholeLifePaymentOptions.RTRPremium;
              break;
            case 'TIR':
              title = false;
              break;
            default:
              title = 'Accelerated Death Benefit for Terminal Illness Rider';
              break;
          }
          if (title !== false) {
            riderHtml =
              riderHtml +
              ns.HtmlPdfNarrativeSummaryRider.replace(/{{Title}}/, title).replace(
                /{{Data}}/,
                Utils.formatCurrency(currPremium)
              );
          }
        });
      }

      return riderHtml;
    }

    function getWaiverPremium(wholeLifePaymentOptions) {
      return (
        wholeLifePaymentOptions.waiverPremium +
        (wholeLifePaymentOptions.GIRWaiverPremium || 0) +
        (wholeLifePaymentOptions.RTRWaiverPremium || 0) +
        (wholeLifePaymentOptions.CIRWaiverPremium || 0)
      );
    }

    //  loops through riders from request and if they exsists it adds them to the coverage information breakdown
    // applies a HtmlPdfNarrativeSummaryRider template for each rider present
    function getCoverageInformationRiders() {
      let riderHtml = '';
      illustrationRequest.riders.forEach((rider) => {
        let title;
        switch (rider.type) {
          case 'GIR':
            title = false;
            break;
          case 'ALIR':
            title = 'ALIR Scheduled Coverage Amount (At Issue)';
            break;
          case 'RTR':
            title = 'RTR Face Amount';
            break;
          case 'TIR':
            title = false;
            break;
        }
        if (title !== false) {
          riderHtml =
            riderHtml +
            ns.HtmlPdfNarrativeSummaryRider.replace(/{{Title}}/, title).replace(
              /{{Data}}/,
              Utils.formatCurrency(rider.amount)
            );
        }
      });

      return riderHtml;
    }

    function getWholeLifeDescription() {
      description = ns.HtmlPdfNarrativeSummaryProductDescriptionWholeLife.replace(
        /{{PRODUCT}}/g,
        `${Product.products[productCode].name} ${term}`
      ).replace(/{{TERM}}/g, `${term}`);
      return description;
    }

    // ******
    // Section : Term Policy functions
    // ****

    //  combines all the other templates/functions into one HTML body that is returned as a string
    function getTermBody() {
      return ns.HtmlPdfNarrativeSummary.replace(/{{ProductDescription}}/g, description)
        .replace(/{{Date}}/, date)
        .replace(/{{Time}}/g, time)
        .replace(/{{ClientFirstName}}/g, illustrationRequest.customerPerson.firstName)
        .replace(/{{ClientLastName}}/g, illustrationRequest.customerPerson.lastName)
        .replace(
          /{{ClientGender}}/g,
          HtmlPdfUtils.getGenderInPrettyFormat(illustrationRequest.gender, illustrationRequest.state)
        )
        .replace(
          /{{ClientAge}}/g,
          CoverpathUtils.getInsuranceAge(illustrationRequest.dob, illustrationRequest.startDate)
        )
        .replace(/{{UnderwritingClass}}/g, RateClasses[illustrationRequest.rateClass].desc)
        .replace(/{{Product}}/g, `${longName}`)
        .replace(/{{PolicyFormNumber}}/g, getTermPolicyFormNumber())
        .replace(/{{PolicyNumber}}/g, illustrationRequest.policyNumber)
        .replace(/{{PolicyDate}}/g, Utils.formatDateLong(new Date(illustrationRequest.startDate)))
        .replace(/{{PaymentFrequency}}/g, Utils.capitalizeFirstLetter(illustrationRequest.paymentFrequency))
        .replace(
          /{{Premium}}/g,
          `$${Utils.formatNumber(
            Utils.round(paymentOptions[illustrationRequest.paymentFrequency.toLowerCase().replace(/-/g, '')].total, 2),
            2
          )}`
        )
        .replace(/{{DeathBenefit}}/g, `$${displayFaceAmount}`);
    }

    function getWholeLifePolicyName() {
      switch (term) {
        case 10:
        case 12:
        case 15:
        case 20:
          return `Limited Payment Whole Life Premiums Payable for ${term} Years`;
        case 65:
        case 100:
          return `Limited Payment Whole Life Premiums Payable to Age ${term}`;
      }
    }

    //Returns policy  form number based on state
    function getTermPolicyFormNumber() {
      if (isCAS) {
        return HtmlPdfUtils.getCASTermPolicyFormNumber(illustrationRequest.state);
      } else if (productCode === Product.products.MMTRADTerm.code) {
        return HtmlPdfUtils.getVantageTermPolicyFormNumber(illustrationRequest.state, productBlock);
      } else {
        return HtmlPdfUtils.getVantageTermECPPolicyFormNumber(illustrationRequest.state);
      }
    }

    // Returns the proper phrase based on the term for the policy statement
    function getPayableTermPhrase(): string {
      switch (term) {
        case 10:
        case 12:
        case 15:
        case 20:
          return `for ${term} years`;
        case 65:
        case 100:
          return `to Age ${term}`;
      }
    }

    function getTermDescription() {
      switch (productBlock) {
        case 'MMOPMTerm_2005b1':
        case 'MMOPMTerm_2004b1':
          longName = 'Term 20 Guaranteed (T20G)';
          description = ns.HtmlPdfNarrativeSummaryProductDescriptionMMOPMTerm;
          description = description
            .replace(/{{AGE}}/g, `${productBlock === 'MMOPMTerm_2005b1' ? 100 : 90}`)
            .replace(/{{END}}/g, `${productBlock === 'MMOPMTerm_2005b1' ? 10 : 20}`);
          break;
        case 'MMOPMTermAPT80_2006b1':
          longName = 'Annual Renewable Term (ART)';
          description = ns.HtmlPdfNarrativeSummaryProductDescriptionMMOPMART;
          break;
        case 'MMOPMTermAPT80_2000b1':
          longName = 'Adjustable Premium Term to age 80 (APT80)';
          description = ns.HtmlPdfNarrativeSummaryProductDescriptionAPT80;
          break;
        case 'MMOPMTerm_2000b1':
          longName = 'Term 20 Guaranteed -99 (T20G-99)';
          description = ns.HtmlPdfNarrativeSummaryProductDescriptionT20G99;
          break;
        default:
          // Determine which product description and replace variables
          // ART: term === 1? Check with Aldo
          longName = `${Product.products[productCode].name} ${term === 1 ? 'ART' : term}${
            productCode === Product.products.CASTermECP.code ? ' ECP' : ''
          }`;
          if (productCode === Product.products.CASTermECP.code) {
            if (term === 15) {
              description = ns.HtmlPdfNarrativeSummaryProductDescriptionECP15;
            } else {
              description = ns.HtmlPdfNarrativeSummaryProductDescriptionECP;
            }
          } else if (productCode === Product.products.MMTRADTermECP.code) {
            longName = `${Product.products[productCode].name} - ${term} ECP`;
            description = ns.HtmlPdfNarrativeSummaryProductDescriptionMMTRADTermECP.replace(
              /{{PRODUCT}}/g,
              longName
            ).replace(/{{TERM}}/g, `${term}`);
            longName += ` (VT-${term} ECP)`;
          } else if (term === 1) {
            description = ns.HtmlPdfNarrativeSummaryProductDescriptionART;
          } else {
            description = ns.HtmlPdfNarrativeSummaryProductDescription;
          }
          description = description.replace(/{{PRODUCT}}/g, longName).replace(/{{TERM}}/g, `${term}`);
      }
      return description;
    }
  }

  async getHtmlContent_NarrativeSummary_PageTwo(
    illustrationRequest: IllustrationRequest,
    inforceProductType: ProductType,
    mecYear: number,
    supplementalMecYear: number | null,
    productBlock?: string
  ) {
    const date = CoverpathUtils.getDateInPrettyFormat();
    const timezone = await CommonUtilsBackend.getTimezoneIdByZipcode(illustrationRequest.agent.address.zip);
    const time = CoverpathUtilsBackEnd.getTimeInPrettyFormat(timezone);
    const term = illustrationRequest.term;
    const productCode = illustrationRequest.productCode;
    const isCAS = HtmlPdfUtils.isCASProduct(productCode);
    const ns = await HtmlPdfUtils.getContent(this.templateFolder, new NarrativeSummary());
    let body;
    let shortName = 'shortName';
    const style = `${ns.css.HtmlPdfNarrativeSummaryStyle}`;
    if (inforceProductType === ProductType.Term) {
      if (productCode === Product.products.MMTRADTermECP.code) {
        shortName = `VT-${term} ECP`;
      } else {
        shortName = `${Product.products[productCode].name} ${term === 1 ? 'ART' : term}${
          productCode === Product.products.CASTermECP.code ? ' ECP' : ''
        }`;
      }
      getTermPageTwo();
    } else if (inforceProductType === ProductType.WholeLife) {
      getWholeLifePageTwo();
    }

    return HtmlPdfUtils.getHtmlContent(style, body);

    function getWholeLifePageTwo() {
      body = ns.HtmlPdfNarrativeSummaryPageTwoWholeLife.replace(/{{RiderDescription}}/, getRiderDescription())
        .replace(
          /{{WaiverOfPremiumDescription}}/,
          illustrationRequest.waiverUnits > 0 ? ns.HtmlPdfNarrativeSummaryWaiverDescription : ''
        )
        .replace(
          /{{MECStatement}}/,
          illustrationRequest.mecStatus === 'Yes'
            ? ns.HtmlPdfNarrativeSummaryPageTwoMec.replace(/{{MecYear}}/g, mecYear)
            : ns.HtmlPdfNarrativeSummaryPageTwoNoMec
        )
        .replace(
          /{{SupplementalMec}}/g,
          illustrationRequest.newFaceAmount > 0
            ? supplementalMecYear > 0
              ? ns.HtmlPdfNarrativeSummaryPageTwoSupplementalMec.replace(
                  /{{SupplementalMecYear}}/g,
                  supplementalMecYear
                )
              : ns.HtmlPdfNarrativeSummaryPageTwoSupplementalNoMec.replace(
                  /{{NoMecYear}}/,
                  `${Utils.getActualAge(illustrationRequest.startDate) + 2}`
                )
            : ''
        )
        .replace(/{{MECStatus}}/, illustrationRequest.mecStatus)
        .replace(/{{NoMecYear}}/, `${Utils.getActualAge(illustrationRequest.startDate) + 2}`);
    }

    function getRiderDescription() {
      let riderDescriptions = '';
      illustrationRequest.riders.forEach((rider) => {
        riderDescriptions =
          riderDescriptions +
          ns.HtmlPdfNarrativeSummaryRiderDefinition.replace(
            /{{RiderTitle}}/,
            riderDefinition[rider.type].title
          ).replace(/{{RiderDescription}}/, riderDefinition[rider.type].description);
      });
      riderDescriptions = riderDescriptions.replace(
        /{{RTR_END_YEAR}}/g,
        (95 - CoverpathUtils.getInsuranceAge(illustrationRequest.dob, illustrationRequest.startDate)).toString()
      );
      return riderDescriptions;
    }
    function getTermPageTwo() {
      body = ns.HtmlPdfNarrativeSummaryPageTwo.replace(/{{Date}}/g, date)
        .replace(/{{Time}}/g, time)
        .replace(
          /{{WaiverOfPremiumDescription}}/,
          illustrationRequest.waiverUnits > 0 ? ns.HtmlPdfNarrativeSummaryWaiverDescription : ''
        )
        .replace(
          /{{AcceleratedDeathBenefitDescription}}/,
          illustrationRequest.hasABR ? ns.HtmlPdfNarrativeSummaryABRDescription : ''
        )
        .replace(/{{RiderList}}/, illustrationRequest.waiverUnits > 0 ? ns.HtmlPdfNarrativeSummaryRiderList : '')
        .replace(
          /{{FaceAmountDecrease}}/,
          illustrationRequest.newFaceAmount > 0 && illustrationRequest.faceAmount > illustrationRequest.newFaceAmount
            ? ns.HtmlPdfNarrativeSummaryFaceAmountDecrease
            : ''
        )
        .replace(/{{ShortProductDescription}}/, GetShortSummaryDescription());

      function GetShortSummaryDescription() {
        switch (productBlock) {
          case mmopmProductBlocks.MMOPMTerm[2005]:
          case mmopmProductBlocks.MMOPMTerm[2004]:
            return ns.HtmlPdfNarrativeSummaryShortT20GDescription;
          case mmopmProductBlocks.MMOPMTerm[99]:
            return ns.HtmlPdfNarrativeSummaryShortT20G99Description;
          case mmopmProductBlocks.MMOPMTermAPT80[2006]:
            return ns.HtmlPdfNarrativeSummaryShortOPMARTDescription;
          case mmopmProductBlocks.MMOPMTermAPT80[99]:
            return ns.HtmlPdfNarrativeSummaryShortAPT80Description;
          default:
            return term === 1
              ? ns.HtmlPdfNarrativeSummaryShortARTDescription.replace(/{{ShortProductName}}/, shortName)
              : ns.HtmlPdfNarrativeSummaryShortProductDescription.replace(/{{ShortProductName}}/, shortName)
                  .replace(/{{Term}}/, term)
                  .replace(/{{TermPlusOne}}/, term + 1);
        }
      }
    }
  }

  buildColumnHeaderDefinitions(
    tableColumns: string[],
    supplementalTableColumns: string[],
    hasFaceChange: boolean
  ): string {
    // If there is a face amount change, show all relevant column header definitions
    // Check for any supplemental column overrides
    const overrides = [];
    for (const column of supplementalTableColumns) {
      const columnValue = wholeLifeColumns[column];
      if (columnValue.supplementalOverride) {
        overrides.push(columnValue.supplementalOverride);
      }
    }
    const supplementalColumnsWithOverrides = supplementalTableColumns.concat(overrides);
    const currentColumns = hasFaceChange
      ? new Set(tableColumns.concat(supplementalColumnsWithOverrides))
      : tableColumns;
    const columnDefs = Array.from(currentColumns);
    columnDefs.sort((a: string, b: string) =>
      wholeLifeColumns[a].definition.title.localeCompare(wholeLifeColumns[b].definition.title)
    );
    let columnDefinitionString = '';
    let prevColumnTitle = null;
    for (const column of columnDefs) {
      const columnValue = wholeLifeColumns[column];
      // Some fields share a column definition ex. contractPremiumBegYear and guaranteedContractPremiumBegYear
      // Check titles to make sure there aren't any duplicate definitions
      if (columnValue.definition.title === prevColumnTitle) {
        continue;
      }

      columnDefinitionString += `${columnHeaderDefinitions.parse(columnValue.definition)}\n`;
      prevColumnTitle = columnValue.definition.title;
    }

    // IFI currently does not support CIR, so we can remove any mention
    return columnDefinitionString
      .replace(/{{CirAddPaidupAdditionsEndYear}}/g, '')
      .replace(/{{CirAddTotalCashValueEndYear}}/g, '')
      .replace(/{{CirAddTotalDeathBenefitEndYear}}/g, '');
  }

  async getHtmlContent_NarrativeSummary_PageThree(
    illustrationRequest: IllustrationRequest,
    inforceProductType: ProductType,
    tableColumns: string[],
    supplementalTableColumns: string[],
    loanInterestRate: number,
    issuedDate?: string,
    productBlock?: string
  ) {
    const ns = await HtmlPdfUtils.getContent(this.templateFolder, new NarrativeSummary());
    const style = `${ns.css.HtmlPdfNarrativeSummaryStyle}`;
    const date = CoverpathUtils.getDateInPrettyFormat();
    const timezone = await CommonUtilsBackend.getTimezoneIdByZipcode(illustrationRequest.agent.address.zip);
    const time = CoverpathUtilsBackEnd.getTimeInPrettyFormat(timezone);
    const productCode = illustrationRequest.productCode;

    let body: string;

    if (inforceProductType === ProductType.Term) {
      body = ns.HtmlPdfNarrativeSummaryPageThree.replace(
        /{{WaiverOfPremiumColumnDescription}}/,
        illustrationRequest.waiverUnits > 0 ? ns.HtmlPdfNarrativeSummaryWaiverColumnDescription : ''
      ).replace(
        /{{MMOPMTermNonGauranteedDefinitions}}/,
        productBlock === mmopmProductBlocks.MMOPMTerm[99]
          ? ns.HtmlPdfNarrativeSummaryMMOPMTermNonGuaranteedColumnDescriptions
          : productBlock === mmopmProductBlocks.MMOPMTermAPT80[99]
          ? ns.HtmlPdfNarrativeSummaryMMOPMAPT80NonGuaranteedColumnDescriptions
          : ''
      );
    } else if (inforceProductType === ProductType.WholeLife) {
      body = ns.HtmlPdfNarrativeSummaryPageThreeWholeLife.replace(/{{Date}}/g, date)
        .replace(/{{Time}}/g, time)
        .replace(
          /{{FaceAmountChange}}/g,
          illustrationRequest.newFaceAmount > 0
            ? ns.HtmlPdfNarrativeSummaryWholeLifeFaceChange.replace(
                /{{OriginalFaceAmount}}/g,
                Utils.formatCurrency(illustrationRequest.faceAmount, 2, true)
              )
                .replace(/{{NewFaceAmount}}/g, Utils.formatCurrency(illustrationRequest.newFaceAmount, 2, true))
                .replace(/{{FaceChangeDate}}/g, CoverpathUtils.getUTCDateString(illustrationRequest.asOfDate))
            : ''
        )
        .replace(
          /{{Riders}}/g,
          illustrationRequest.riders
            ? 'Annual premium figures include premiums for the following riders: ' + getRiderList()
            : ''
        )
        .replace(
          /{{FaceAmountDecreaseDisclaimer}}/g,
          illustrationRequest.newFaceAmount > 0 && illustrationRequest.faceAmount > illustrationRequest.newFaceAmount
            ? ns.HtmlPdfNarrativeSummaryWholeLifeFaceDecreaseDisclaimer
            : ''
        )
        .replace(/{{PolicyInterestRate}}/g, (loanInterestRate * 100).toFixed(2))
        .replace(/{{State}}/g, illustrationRequest.state)
        .replace(/{{IssuedDate}}/g, HtmlPdfUtils.getDateInPrettyFormatFromString(issuedDate))
        .replace(
          /{{ColumnHeaderDefinitions}}/g,
          this.buildColumnHeaderDefinitions(
            tableColumns,
            supplementalTableColumns,
            illustrationRequest.newFaceAmount > 0
          )
        );
    }

    function getRiderList() {
      let riderList = '';
      illustrationRequest.riders.forEach((rider, index) => {
        if (index === 0) {
          riderList = riderList + rider.type;
        } else {
          riderList = riderList + ', ' + rider.type;
        }
      });

      return riderList;
    }

    return HtmlPdfUtils.getHtmlContent(style, body);
  }
}
